import React, { useState, useMemo } from 'react';
import { BlockPerformance } from '../../services/blockPerformanceService';
import { SyncStatusBadge } from './SyncStatusBadge';

interface BlockMetricsTableProps {
  blocks: BlockPerformance[];
  onExport?: (format: 'csv' | 'pdf') => void;
}

type SortField = keyof BlockPerformance;
type SortDirection = 'asc' | 'desc';

export const BlockMetricsTable: React.FC<BlockMetricsTableProps> = ({ blocks, onExport }) => {
  const [sortField, setSortField] = useState<SortField>('block_name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const sortedBlocks = useMemo(() => {
    return [...blocks].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      const numA = Number(aVal);
      const numB = Number(bVal);
      return sortDirection === 'asc' ? numA - numB : numB - numA;
    });
  }, [blocks, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getCellColor = (value: number, field: string) => {
    if (field === 'dc_cuf' || field === 'ac_cuf' || field === 'pr') {
      return value < 70 ? 'text-red-600' : value < 85 ? 'text-yellow-600' : 'text-green-600';
    }
    return '';
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b flex justify-between items-center">
        <h3 className="text-lg font-semibold">Block Performance Metrics</h3>
        {onExport && (
          <div className="flex gap-2">
            <button 
              onClick={() => onExport('csv')}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Export CSV
            </button>
            <button 
              onClick={() => onExport('pdf')}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              Export PDF
            </button>
          </div>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {[
                { key: 'block_name', label: 'Block Name' },
                { key: 'dc_cuf', label: 'DC CUF (%)' },
                { key: 'ac_cuf', label: 'AC CUF (%)' },
                { key: 'pr', label: 'PR (%)' },
                { key: 'daily_yield', label: 'Daily Yield (kWh/kWp)' },
                { key: 'inverter_generation', label: 'Inverter Gen (kWh)' },
                { key: 'meter_generation', label: 'Meter Gen (kWh)' },
                { key: 'sync_status', label: 'Sync Status' }
              ].map(({ key, label }) => (
                <th 
                  key={key}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort(key as SortField)}
                >
                  <div className="flex items-center gap-1">
                    {label}
                    {sortField === key && (
                      <span className="text-blue-600">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedBlocks.map((block) => (
              <tr key={block.block_name} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{block.block_name}</td>
                <td className={`px-4 py-3 ${getCellColor(block.dc_cuf, 'dc_cuf')}`}>
                  {block.dc_cuf.toFixed(1)}
                </td>
                <td className={`px-4 py-3 ${getCellColor(block.ac_cuf, 'ac_cuf')}`}>
                  {block.ac_cuf.toFixed(1)}
                </td>
                <td className={`px-4 py-3 ${getCellColor(block.pr, 'pr')}`}>
                  {block.pr.toFixed(1)}
                </td>
                <td className="px-4 py-3">{block.daily_yield.toFixed(2)}</td>
                <td className="px-4 py-3">{block.inverter_generation.toLocaleString()}</td>
                <td className="px-4 py-3">{block.meter_generation.toLocaleString()}</td>
                <td className="px-4 py-3">
                  <SyncStatusBadge 
                    syncStatus={block.sync_status} 
                    deviation={block.sync_deviation}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};